import React from "react";
import { Provider } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment-timezone";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { trackClicks, trackRouter } from "./gtag";

import store from "./redux/store";
import "./App.css";
import Admin from "./components/Admin";
import AdminHome from "./components/AdminHome";
import AdminEvents from "./components/AdminEvents";
import LandingPage from "./components/Login/LandingPage";
import ErrorPage from "./components/ErrorPage";
import AdminEventView from "./components/AdminEventView/AdminEventView";
import EventDashboard from "./components/AdminEventView/EventDashboard";
import EventBasicInfo from "./components/AdminEventView/EventBasicInfo/EventBasicInfo";
import EventMedia from "./components/AdminEventView/EventMedia/EventMedia";
import EventOrderForms from "./components/AdminEventView/EventOrderForms/EventOrderForms";
import EventTicketTypes from "./components/AdminEventView/EventTIcketTypes/EventTicketTypes";
import { OrderFormsList } from "./components/AdminEventView/EventOrderForms/OrderFormsList";
import { EditOrderFormItemForm } from "./components/AdminEventView/EventOrderForms/EditOrderFormItemForm";
import { TicketTypesList } from "./components/AdminEventView/EventTIcketTypes/TicketTypesList";
import { EditTicketTypeForm } from "./components/AdminEventView/EventTIcketTypes/EditTicketTypeForm";
import EventsList from "./components/AdminEvents/EventsList";
import Organizations from "./components/Admin/Organizations/Organizations";
import OrganizationBasicInfo from "./components/Admin/Organizations/OrganizationBasicInfo";
import Payments from "./components/Admin/Organizations/Payments";
import Orders from "./components/Admin/Organizations/Orders";
import OrderDetailsPage from "./components/Admin/Organizations/Orders/OrderDetailsPage";
import Team from "./components/Admin/Organizations/Team";
import EditTeamMember from "./components/Admin/Organizations/Team/EditTeamMember";
import { UserList } from "./components/Admin/Organizations/Team/UserList";
import TransferOwnership from "./components/Admin/Organizations/Team/TransferOwnership";
import Pixels from "./components/Admin/Organizations/Pixels";
import AcceptInvitation from "./components/Admin/Organizations/AcceptInvitation";
import AcceptOwnershipTransfer from "./components/Admin/Organizations/AcceptOwnershipTransfer";
import EventPublishing from "./components/AdminEventView/EventPublishing";
import EventTickets from "./components/AdminEventView/EventTickets";
import EventOrders from "./components/AdminEventView/EventOrders";
import OrderDetails from "./components/AdminEventView/EventOrders/OrderDetails";
import Shows from "./components/Admin/Organizations/Shows";
import ShowDetails from "./components/Admin/Organizations/Shows/ShowDetails";
import ShowsList from "./components/Admin/Organizations/Shows/ShowsList";
import ShowBasicInfo from "./components/Admin/Organizations/Shows/ShowBasicInfo";
import ShowMedia from "./components/Admin/Organizations/Shows/ShowMedia/ShowMedia";
import Performers from "./components/Admin/Organizations/Performers/Performers";
import PerformersList from "./components/Admin/Organizations/Performers/PerformersList/PerformersList";
import PerformerFormPage from "./components/Admin/Organizations/Performers/PerformerFormPage";
import Rooms from "./components/Admin/Organizations/Rooms";
import RoomDetails from "./components/Admin/Organizations/Rooms/RoomDetails/RoomDetails";
import RoomsList from "./components/Admin/Organizations/Rooms/RoomsList/RoomsList";
import RoomBasicInfo from "./components/Admin/Organizations/Rooms/RoomBasicInfo/RoomBasicInfo";
import RoomMedia from "./components/Admin/Organizations/Rooms/RoomMedia/RoomMedia";
import Attendees from "./components/Admin/Organizations/Attendees/Attendees";
import AttendeesList from "./components/Admin/Organizations/Attendees/AttendeesList";
import AttendeeDetail from "./components/Admin/Organizations/Attendees/AttendeeDetail";
import EventPromoCodes from "./components/AdminEventView/EventPromoCodes";
import PromoCodesList from "./components/AdminEventView/EventPromoCodes/PromoCodesList";
import EditPromoCodeForm from "./components/AdminEventView/EventPromoCodes/EditPromoCodeForm/EditPromoCodeForm";
import EventAffiliates from "./components/AdminEventView/EventAffiliates/EventAffiliates";
import AffiliatesList from "./components/AdminEventView/EventAffiliates/AffiliatesList/AffiliatesList";
import EditAffiliateForm from "./components/AdminEventView/EventAffiliates/EditAffiliateForm/EditAffiliateForm";
import SessionsList from "./components/AdminEventView/EventSessions/SessionsList/SessionsList";
import EditSessionForm from "./components/AdminEventView/EventSessions/EditSessionForm/EditSessionForm";
import EventSessions from "./components/AdminEventView/EventSessions/EventSessions";
import {
  CRMRoute,
  CRM,
  CRMDashboard,
  CRMOrganizations,
  CRMOrganizationLayout,
  CRMOrganizationDetails,
  CRMUsers,
  CRMUserDetails,
  CRMEvents,
  CRMOrders,
  CrmOrderDetailsPage,
  CrmOrdersLayout,
  CRMPayments,
  CRMMaintenance,
} from "./components/Admin/CRM";
import {
  DevToolsRoute,
  DevTools,
  SeedTestData,
} from "./components/Admin/DevTools";

const router = createBrowserRouter([
  {
    path: "/",
    element: <Admin />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "login",
        element: <LandingPage />,
      },
      {
        path: "acceptInvitation/:invitationId",
        element: <AcceptInvitation />,
      },
      {
        path: "acceptOwnershipTransfer/:transferId",
        element: <AcceptOwnershipTransfer />,
      },
      {
        path: "events",
        element: <AdminEvents />,
        children: [
          {
            index: true,
            element: <EventsList />,
          },
          {
            path: "new",
            element: <AdminEventView />,
            children: [
              {
                path: "",
                element: <EventBasicInfo />,
              },
            ],
          },
          {
            path: ":eventId",
            element: <AdminEventView />,
            children: [
              {
                path: "basic",
                element: <EventBasicInfo />,
              },
              {
                path: "media",
                element: <EventMedia />,
              },
              {
                path: "orderForms",
                element: <EventOrderForms />,
                children: [
                  {
                    index: true,
                    element: <OrderFormsList />,
                  },
                  {
                    path: ":orderFormId",
                    element: <EditOrderFormItemForm />,
                  },
                ],
              },
              {
                path: "promoCodes",
                element: <EventPromoCodes />,
                children: [
                  {
                    index: true,
                    element: <PromoCodesList />,
                  },
                  {
                    path: ":promoCodeId",
                    element: <EditPromoCodeForm />,
                  },
                ],
              },
              {
                path: "affiliates",
                element: <EventAffiliates />,
                children: [
                  {
                    index: true,
                    element: <AffiliatesList />,
                  },
                  {
                    path: ":affiliateId",
                    element: <EditAffiliateForm />,
                  },
                ],
              },
              {
                path: "sessions",
                element: <EventSessions />,
                children: [
                  {
                    index: true,
                    element: <SessionsList />,
                  },
                  {
                    path: ":sessionId",
                    element: <EditSessionForm />,
                  },
                ],
              },
              {
                path: "ticketTypes",
                element: <EventTicketTypes />,
                children: [
                  {
                    index: true,
                    element: <TicketTypesList />,
                  },
                  {
                    path: ":ticketTypeId",
                    element: <EditTicketTypeForm />,
                  },
                ],
              },
              { path: "publish", element: <EventPublishing /> },
              { path: "orders", element: <EventOrders /> },
              {
                path: "orders/:orderId",
                element: <OrderDetails />,
              },
              { path: "tickets", element: <EventTickets /> },
              {
                path: "dashboard",
                element: <EventDashboard />,
              },
            ],
          },
        ],
      },
      {
        path: "organizations/:organizationId",
        element: <Organizations />,
        children: [
          { index: true, element: <OrganizationBasicInfo /> },
          {
            path: "team",
            element: <Team />,
            children: [
              { index: true, element: <UserList /> },
              { path: "invite", element: <EditTeamMember /> },
              { path: "transfer", element: <TransferOwnership /> },
              { path: "users/:userId", element: <EditTeamMember /> },
              {
                path: "invitations/:invitationId",
                element: <EditTeamMember />,
              },
            ],
          },
          { path: "payments", element: <Payments /> },
          { path: "pixels", element: <Pixels /> },
          {
            path: "performers",
            element: <Performers />,
            children: [
              { index: true, element: <PerformersList /> },
              { path: "new", element: <PerformerFormPage /> },
              { path: ":performerId/edit", element: <PerformerFormPage /> },
            ],
          },
          {
            path: "shows",
            element: <Shows />,
            children: [
              { index: true, element: <ShowsList /> },
              {
                path: "new",
                element: <ShowDetails />,
                children: [{ index: true, element: <ShowBasicInfo /> }],
              },
              {
                path: ":showId",
                element: <ShowDetails />,
                children: [
                  { path: "basic", element: <ShowBasicInfo /> },
                  { path: "media", element: <ShowMedia /> },
                ],
              },
            ],
          },
          {
            path: "rooms",
            element: <Rooms />,
            children: [
              { index: true, element: <RoomsList /> },
              {
                path: "new",
                element: <RoomDetails />,
                children: [{ index: true, element: <RoomBasicInfo /> }],
              },
              {
                path: ":roomId",
                element: <RoomDetails />,
                children: [
                  { path: "basic", element: <RoomBasicInfo /> },
                  { path: "media", element: <RoomMedia /> },
                ],
              },
            ],
          },
          {
            path: "attendees",
            element: <Attendees />,
            children: [
              { index: true, element: <AttendeesList /> },
              { path: ":attendeeId", element: <AttendeeDetail /> },
            ],
          },
        ],
      },
      {
        path: "dashboard",
        element: <AdminHome />,
      },
      { path: "orders", element: <Orders /> },
      {
        path: "orders/:orderId",
        element: <OrderDetailsPage />,
      },
      {
        path: "crm",
        element: <CRMRoute />,
        children: [
          {
            element: <CRM />,
            children: [
              { index: true, element: <CRMDashboard /> },
              { path: "organizations", element: <CRMOrganizations /> },
              {
                path: "organizations/:organizationId",
                element: <CRMOrganizationLayout />,
                children: [
                  {
                    index: true,
                    element: <CRMOrganizationDetails />,
                  },
                ],
              },
              { path: "users", element: <CRMUsers /> },
              { path: "users/:userId", element: <CRMUserDetails /> },
              { path: "events", element: <CRMEvents /> },
              {
                path: "orders",
                element: <CrmOrdersLayout />,
                children: [
                  { index: true, element: <CRMOrders /> },
                  {
                    path: ":organizationId/:orderId",
                    element: <CrmOrderDetailsPage />,
                  },
                ],
              },
              { path: "payments", element: <CRMPayments /> },
              { path: "maintenance", element: <CRMMaintenance /> },
            ],
          },
        ],
      },
      {
        path: "dev-tools",
        element: <DevToolsRoute />,
        children: [
          {
            element: <DevTools />,
            children: [{ index: true, element: <SeedTestData /> }],
          },
        ],
      },
    ],
  },
]);

trackRouter(router);
trackClicks();

export default function App() {
  return (
    <Provider store={store}>
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        dateLibInstance={moment}
      >
        <RouterProvider router={router} />
      </LocalizationProvider>
    </Provider>
  );
}
